import { WorkflowNodeType } from './types';

/**
 * The dataset write tool's key. Hardcoded rather than imported from
 * `@cotera/api` (`COCO_DATASET_WRITE_TOOL_KEY`) to keep this leaf transform free
 * of the coco tool graph; it is a stable public tool id.
 */
const DATASET_WRITE_TOOL_KEY = 'coco/dataset-write';

/**
 * A dataset-trigger write-back names the row that fired the run by the trigger
 * payload's declared row key, `__trigger__/__meta/rowKey`. Inside the nested
 * `rows[]` param it is written as a `{{ref}}` template token. `__trigger__` is
 * `WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID`; `__meta` is `DATASET_RUN_INPUT_META_KEY`.
 * Matched leniently on inner whitespace, since both `{{ref}}` and `{{ ref }}`
 * have been emitted.
 */
const TRIGGER_ROW_KEY_TOKEN = /^\{\{\s*__trigger__\/__meta\/rowKey\s*\}\}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const isTriggerRowKeyBinding = (value: unknown): boolean =>
  typeof value === 'string' && TRIGGER_ROW_KEY_TOKEN.test(value.trim());

const isNonEmptyString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Moves a dataset write's `rows[].rowId` trigger-row-key binding to
 * `rows[].rowKey`, in place of the old integer-shape guess.
 *
 * `__trigger__/__meta/rowKey` is the dataset's *declared* row key, which is a
 * business value on a keyed dataset — not the artifact's internal id — so bound
 * to `rowId` it fails (or writes the wrong row) on such a dataset. `rowKey`
 * resolves it through the declared key columns, by value rather than shape.
 *
 * Pure and idempotent: only a `rowId` holding exactly the trigger row-key token
 * is moved, only when `rowKey` is not already set, and the move clears `rowId`
 * so a second pass finds nothing. Returns `null` when the params are unchanged,
 * so callers can cheaply detect a no-op. Any other `rowId` — a literal, an
 * internal id read from the dataset, a ref to some other node — is left alone.
 */
export function migrateDatasetWriteRowKeyParams(
  toolKey: unknown,
  params: unknown
): unknown | null {
  if (toolKey !== DATASET_WRITE_TOOL_KEY || !isRecord(params)) {
    return null;
  }
  const rows = params['rows'];
  if (!Array.isArray(rows)) {
    return null;
  }
  let changed = false;
  const nextRows = rows.map((row) => {
    if (
      !isRecord(row) ||
      !isTriggerRowKeyBinding(row['rowId']) ||
      isNonEmptyString(row['rowKey'])
    ) {
      return row;
    }
    changed = true;
    return { ...row, rowKey: row['rowId'], rowId: null };
  });
  return changed ? { ...params, rows: nextRows } : null;
}

/** One `rows[].rowId` → `rows[].rowKey` move, for reporting. */
export type DatasetWriteRowKeyMigration = {
  readonly nodeId: string;
};

/** Minimal graph node shape the migration needs (definition or draft graph). */
export type DatasetWriteRowKeyNode = {
  readonly id: string;
  readonly type: string;
  readonly data?: unknown;
};

/**
 * Applies {@link migrateDatasetWriteRowKeyParams} to every dataset-write
 * tool-call node in a graph. The node-level counterpart the editor heal runs, so
 * an opened workflow persists the migration; the runner applies the params-level
 * function directly so a trigger-only workflow self-heals on its next run
 * without waiting to be opened.
 *
 * Nodes with nothing to fix are returned by identity, so callers can skip a
 * re-render on a healthy graph.
 */
export function migrateDatasetWriteRowKeyBindings<
  T extends DatasetWriteRowKeyNode
>(
  nodes: readonly T[]
): {
  readonly nodes: readonly T[];
  readonly migrations: readonly DatasetWriteRowKeyMigration[];
} {
  const migrations: DatasetWriteRowKeyMigration[] = [];
  const out = nodes.map((node) => {
    if (node.type !== WorkflowNodeType.ToolCall || !isRecord(node.data)) {
      return node;
    }
    const nextParams = migrateDatasetWriteRowKeyParams(
      node.data['key'],
      node.data['params']
    );
    if (nextParams === null) {
      return node;
    }
    migrations.push({ nodeId: node.id });
    return { ...node, data: { ...node.data, params: nextParams } };
  });
  return { nodes: out, migrations };
}
